import { MosaicTile, RawVisionFrame, ValidatedFrame } from '../architecture/mosaic_pipeline';

export interface LandmarkPoint {
  x: number;
  y: number;
  z: number;
}

export const HAND_IK_CHAINS: ReadonlyArray<readonly [number, number]> = [
  [0, 1], [1, 2], [2, 3], [3, 4],
  [0, 5], [5, 6], [6, 7], [7, 8],
  [0, 9], [9, 10], [10, 11], [11, 12],
  [0, 13], [13, 14], [14, 15], [15, 16],
  [0, 17], [17, 18], [18, 19], [19, 20],
] as const;

export type KinematicBaseline = ReadonlyArray<number>;

export class KinematicTopologyGateTile implements MosaicTile<RawVisionFrame, ValidatedFrame> {
  public readonly tileId = 'KinematicTopologyGateTile';
  private _baseline: KinematicBaseline | null = null;
  public readonly segmentLengthTolerance: number;

  constructor(config: Readonly<{ tolerance?: number }> = {}) {
    this.segmentLengthTolerance = config.tolerance ?? 0.2;
  }

  public process(frame: RawVisionFrame): ValidatedFrame | null {
    const landmarks = frame.rawLandmarks as LandmarkPoint[] | undefined;
    if (!landmarks || landmarks.length !== 21) {
      return { ...frame, isValidated: true }; // Fail-open if no landmarks or partial tracking
    }

    if (!this._baseline) {
      this.calibrate(landmarks);
      return { ...frame, isValidated: true }; // Fail-open on first frame
    }

    if (!this.check(landmarks)) {
      return null; // KINEMATIC GUILLOTINE: Drop impossible geometry
    }

    return { ...frame, isValidated: true };
  }

  private calibrate(landmarks: LandmarkPoint[]): void {
    if (landmarks.length !== 21) return;
    this._baseline = HAND_IK_CHAINS.map((chain) => {
      const a = chain[0];
      const b = chain[1];
      const lmA = landmarks[a];
      const lmB = landmarks[b];
      if (!lmA || !lmB) return 0;
      return KinematicTopologyGateTile.euclidean(lmA, lmB);
    });
  }

  private check(landmarks: LandmarkPoint[]): boolean {
    if (!this._baseline || landmarks.length !== 21) return true;

    const tol = this.segmentLengthTolerance;

    for (let i = 0; i < HAND_IK_CHAINS.length; i++) {
      const chain = HAND_IK_CHAINS[i];
      if (!chain) continue;
      const a = chain[0];
      const b = chain[1];
      const lmA = landmarks[a];
      const lmB = landmarks[b];
      if (!lmA || !lmB) continue;

      const measured = KinematicTopologyGateTile.euclidean(lmA, lmB);
      const ref = this._baseline[i];

      if (ref === undefined || ref <= 0) continue;

      if (Math.abs(measured - ref) / ref > tol) {
        return false;
      }
    }

    return true;
  }

  static euclidean(a: LandmarkPoint, b: LandmarkPoint): number {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    const dz = a.z - b.z;
    return Math.hypot(dx, dy, dz);
  }
}
